<template>
  <div class="question form-field" :class="'q' + question.order" redesign>
    <!-- Information tooltip -->
    <div class="info-container" v-if="question.information && question.information !== ''">
      <div class="information-icon">
        <img src="@/assets/icons/information-icon.png" />
      </div>
      <div class="tooltip">
        <span class="tooltip-text" v-translate="context">
          {{ question.information }}
        </span>
      </div>
    </div>

    <!-- Introduction -->
    <p class="introduction" v-if="question.introduction && question.introduction !== ''">
      {{ question.introduction }}
    </p>

    <div class="question-and-tooltip-wrapper" v-if="question.qType !== CHECKBOX">
      <!-- Question text -->
      <div class="question-wrapper">
        <h4 class="form-field-header">
          <span v-if="!question.isOptional">*</span>
          {{ question.text | customVariableValue(customVariables) }}
        </h4>
      </div>

      <!-- Description -->
      <div class="description" v-if="question.description">
        <div class="description-tooltip-wrapper">
          <font-awesome-icon :icon="['far', 'question-circle']" :style="{color: '#919EAB', 'margin-left': '0.5rem', 'margin-top': '-1rem'}">
          </font-awesome-icon>
          <div class="description-text" v-html="question.description"></div>
        </div>
      </div>
    </div>

    <!-- Answer fields -->

    <!-- Multiple choice -->
    <div
      class="form-field-container"
      v-if="
        question.qType === MULTIPLE_CHOICE ||
          question.qType === MULTIPLE_CHOICE_ADDITIONAL ||
          question.qType === YES_NO_ADDITIONAL
      "
    >
      <!-- Mobile multiple choice -->
      <select
        :class="{ mobile: !forceMobile }"
        v-if="!isText"
        v-model="question.answer.option"
        @change="onChange"
      >
        <option
          v-for="option in question.answerOptions"
          :key="option.id"
          :value="option.id"
          :data-action="optionTargetMobile(option.text)"
        >
          {{ option.text | customVariableValue(customVariables) }}
        </option>
      </select>
      <span v-if="!isText" class="form-dropdown" :class="{ mobile: !forceMobile }" />

      <!-- Regular (tablet & desktop) -->
      <div v-if="!isText && !forceMobile" class="hidden-mobile">
        <div class="radio-buttons" :class="question.style">
          <label class="radio-button" v-for="option in question.answerOptions" :key="option.id">
            <input
              type="radio"
              v-model="question.answer.option"
              :value="option.id"
              :data-action="optionTargetMobile(option.text)"
            />
            <span :data-action="optionTargetDesktop(option.text)">
              {{ option.text | customVariableValue(customVariables) }}
            </span>
          </label>
        </div>
      </div>

      <!-- Boolean multiple choice -->
      <select
        :class="{ mobile: !forceMobile }"
        v-if="isText"
        v-model="question.answer.text"
        @change="onChange"
      >
        <option
          v-for="option in question.answerOptions"
          :key="option.id"
          :value="option.id"
          :data-action="optionTargetMobile(option.text)"
          >
          {{ option.text | customVariableValue(customVariables) }}
        </option>
      </select>
      <span v-if="isText" class="form-dropdown" :class="{ mobile: !forceMobile }" />

      <!-- Boolean choices (tablet & desktop) -->
      <div v-if="isText && !forceMobile" class="hidden-mobile">
        <div class="radio-buttons" :class="question.style">
          <label class="radio-button" v-for="option in question.answerOptions" :key="option.id">
            <input
              type="radio"
              v-model="question.answer.text"
              :value="option.id"
              :data-action="optionTargetMobile(option.text)"
            />
            <span :data-action="optionTargetDesktop(option.text)">
              {{ option.text | customVariableValue(customVariables) }}
            </span>
          </label>
        </div>
      </div>

      <!-- Additional text field -->
      <template
        v-if="
          (question.qType === MULTIPLE_CHOICE_ADDITIONAL ||
            question.qType === YES_NO_ADDITIONAL) &&
              isAdditionalOrOtherSelectedMultipleChoice
        "
      >
        <div class="form-field-container">
          <textarea
            v-model="question.answer.text"
            class="form-field-textarea"
            @input="mixin_TextAreaResize_resize"
            rows="1"
            @blur="onBlur"
            placeholder="Tell us more..."
            required
          ></textarea>
        </div>
      </template>
    </div>

    <div v-else-if="question.qType === MULTI_SELECT" class="form-field-container">
      <!-- Mobile -->
      <md-field class="md-field-reset" :class="{ mobile: !forceMobile }">
        <md-select v-model="question.answer.options" md-dense multiple @md-selected="onChange">
          <md-option v-for="option in question.answerOptions" :key="option.id" :value="option.id">
            {{ option.text | customVariableValue(customVariables) }}
          </md-option>
        </md-select>
      </md-field>

      <div v-if="!forceMobile" class="checkboxes hidden-mobile">
        <label v-for="option in question.answerOptions" class="checkbox" :key="option.id">
          <input
            type="checkbox"
            :value="question.answer.options.includes(option.id)"
            :checked="question.answer.options.includes(option.id)"
            @click="multiSelectClick(option.id)"
          />
          <span class="form-checkbox" :data-action="optionTargetMobile(option.text)" />
          <span>
            {{ option.text | customVariableValue(customVariables) }}
          </span>
        </label>
      </div>

      <div v-if="isNoOrOtherSelectedMultiSelect">
        <div class="form-field-container">
          <textarea
            v-model="question.answer.text"
            class="form-field-textarea"
            @input="mixin_TextAreaResize_resize"
            rows="1"
            @blur="onBlur"
            placeholder="Tell us more..."
            required
          ></textarea>
        </div>
      </div>
    </div>

    <!-- Textarea -->
    <div v-else-if="question.qType === FREE_TEXT" id="md-textarea">
      <textarea
        class="form-field-textarea"
        @input="mixin_TextAreaResize_resize"
        rows="1"
        @blur="onBlur"
        v-model="question.answer.text"
        :data-action="questionTarget"
        required
      ></textarea>
    </div>

    <!-- Number -->
    <div v-else-if="question.qType === NUMBER" class="form-field-container">
      <input
        type="text"
        class="form-field-text"
        @blur="onBlur"
        v-model="question.answer.text"
        v-mask="inputMask"
        :placeholder="placeholder"
        required
      />
    </div>

    <!-- Date -->
    <div v-else-if="question.qType === DATE">
      <md-field class="input-field">
        <!-- <h5 v-translate>When it happened:</h5> -->
        <md-input
          class="form-field-text"
          v-model="question.answer.text"
          :placeholder="$gettext('MM/YYYY')"
          :style="{ borderBottom: '1px solid gray' }"
          @blur="onBlur"
        ></md-input>
      </md-field>
    </div>

    <!-- Full name -->
    <div v-else-if="question.qType === FULL_NAME" class="full-name">
      <input
        v-model="firstName"
        class="form-field-text"
        type="text"
        placeholder="First name"
        @change="onChange"
        @blue="onBlur"
      />
      <input
        v-model="lastName"
        class="form-field-text"
        type="text"
        placeholder="Last name"
        @change="onChange"
        @blue="onBlur"
      />
    </div>

    <!-- Phone number -->
    <div v-else-if="question.qType === PHONE_NUMBER" class="phone">
      <input
        v-model="question.answer.text"
        class="form-field-text"
        type="text"
        placeholder="(###) ###-####"
        v-mask="'(###) ###-####'"
        @change="onChange"
        @blue="onBlur"
      />
    </div>

    <!-- Email -->
    <div v-else-if="question.qType === EMAIL" class="email">
      <input
        v-model="email"
        class="form-field-text"
        type="text"
        placeholder="email@example.com"
        @change="onChange"
        @blue="onBlur"
      />
      <div class="form-field-container">
        <h4 class="form-field-header">* Re-enter email:</h4>
        <div class="form-field">
          <input
            v-model="confirmEmail"
            class="form-field-text"
            type="text"
            placeholder="email@example.com"
            @change="onChange"
            @blue="onBlur"
          />
        </div>
      </div>
    </div>

    <!-- User zip code -->
    <div v-else-if="question.qType === USER_ZIP_CODE" class="form-field-container">
      <input
        type="text"
        class="form-field-text"
        @blur="onBlur"
        v-model="question.answer.text"
        v-mask="inputMask"
        :placeholder="placeholder"
        required
      />
    </div>

    <!-- Upward Fund valid zip code -->
    <div v-else-if="question.qType === UF_VALID_ZIP_CODE" class="form-field-container">
      <input
        type="text"
        class="form-field-text"
        @blur="onBlur"
        v-model="question.answer.text"
        placeholder="#####"
        required
      />
    </div>

    <!-- Checkbox -->
    <template v-else-if="question.qType === CHECKBOX">
      <div  class="form-field-container checkbox-field">
        <div class="checkboxes">
          <label class="checkbox">
            <input type="checkbox" v-model="question.answer.text" />
            <span class="form-checkbox" />
            <span>{{ question.text | customVariableValue(customVariables) }}</span>
          </label>
        </div>
      </div>
      <div class="help-text">
        <div v-if="question.answer.text !== true" :class="['ruby-font']">
          * <span>{{ $gettext('Required') }}</span>
        </div>
      </div>
    </template>

    <!-- Yes / No, Additional -->
    <!--
    <div v-else-if="question.qType === YES_NO_ADDITIONAL">
      Yes / no, additional...
    </div>
    -->
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Question from '@/services/surveyQuestions'
import TextAreaResize from '../../mixins/TextAreaResize'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

const currencyMask = createNumberMask({
  prefix: '$',
  allowDecimal: true,
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
  allowNegative: false,
})
const numberMask = createNumberMask({
  prefix: '',
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
  allowNegative: false,
})

function keyString(optionText) {
  return (
    optionText
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, '-')
  )
}

export default {
  name: 'Question',
  props: {
    question: {
      type: Object,
      default: function() {
        return {}
      },
    },
    isText: {
      type: Boolean,
      default: false,
    },
    forceMobile: {
      type: Boolean,
      default: false,
    },
    context: {
      type: Object,
      default: function() {
        return {}
      },
    },
  },
  mixins: [TextAreaResize],
  data() {
    return {
      currencyMask: currencyMask,
      numberMask: numberMask,
      zipCodeMask: '#####',
      MULTI_SELECT: Question.options.MULTI_SELECT,
      MULTIPLE_CHOICE: Question.options.MULTIPLE_CHOICE,
      NUMBER: Question.options.NUMBER,
      FREE_TEXT: Question.options.FREE_TEXT,
      DATE: Question.options.DATE,
      FULL_NAME: Question.options.FULL_NAME,
      PHONE_NUMBER: Question.options.PHONE_NUMBER,
      EMAIL: Question.options.EMAIL,
      USER_ZIP_CODE: Question.options.USER_ZIP_CODE,
      MULTIPLE_CHOICE_ADDITIONAL: Question.options.MULTIPLE_CHOICE_ADDITIONAL,
      YES_NO_ADDITIONAL: Question.options.YES_NO_ADDITIONAL,
      UF_VALID_ZIP_CODE: Question.options.UF_VALID_ZIP_CODE,
      CHECKBOX: Question.options.CHECKBOX,

      // Full name field type
      firstName: '',
      lastName: '',

      // Email field type
      email: '',
      confirmEmail: '',
    }
  },
  created() {
    if (this.question.qType === this.FULL_NAME) {
      let full_name_split = this.question.answer.text.split(' ')
      this.firstName = full_name_split.shift()
      this.lastName = full_name_split.join(' ')
    } else if (this.question.qType === this.EMAIL) {
      this.email = this.question.answer.text
      this.confirmEmail = this.question.answer.text
    } else if (this.question.qType === this.USER_ZIP_CODE) {
      this.question.answer.text = this.user.zipCode || ''
    }
  },
  methods: {
    onChange() {
      if (this.question.qType === this.FULL_NAME)
        this.question.answer.text = this.firstName + ' ' + this.lastName

      if (this.question.qType == this.EMAIL)
        if (this.email === this.confirmEmail) this.question.answer.text = this.email

      this.$emit('answer-changed', this.question)
    },
    onBlur() {
      if (this.question.qType === this.FULL_NAME)
        this.question.answer.text = this.firstName + ' ' + this.lastName

      if (this.question.qType == this.EMAIL)
        if (this.email === this.confirmEmail) this.question.answer.text = this.email

      this.$emit('answer-changed', this.question)
    },
    textAreaAdjust(o) {
      o.style.height = '1px'
      o.style.height = 25 + o.scrollHeight + 'px'
    },
    multiSelectClick(optionId) {
      if (this.question.answer.options.includes(optionId)) {
        let optionIndex = this.question.answer.options.indexOf(optionId)
        this.question.answer.options.splice(optionIndex, 1)
      } else {
        this.question.answer.options.push(optionId)
      }
    },
    isSelectedMultiSelect(values) {
      for(let i = 0; i < this.question.answer.options.length; i++) {
        let optionId = this.question.answer.options[i]

        let answerOption = this.question.answerOptions.find(answerOption => answerOption.id == optionId)
        if(values.includes(answerOption.text))
          return true
      }
      return false
    },
    isSelectedMultipleChoice(values) {
      // Get our selected answer option ID
      let optionId = this.question.answer.option

      // Find the option in answerOptions
      let answerOption = this.question.answerOptions.find(answerOption => answerOption.id == optionId)
      return values.includes(answerOption?.text)
    },
    optionTargetMobile(key) {
      const guid = this.question.humanReadableId
      return `survey-${guid}-option-${keyString(key)}-mobile`
    },
    optionTargetDesktop(key) {
      const guid = this.question.humanReadableId
      return `survey-${guid}-option-${keyString(key)}-desktop`
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    customVariables() {
      return this.organizationDetails.customVariables
    },
    isInOptions() {
      return optionId => {
        return this.question.answer.options.includes(optionId)
      }
    },
    answerOptions() {
      return this.question.answer.options
    },
    fullName() {
      return this.firstName + ' ' + this.lastName
    },
    isNoOrOtherSelectedMultiSelect() {
      return this.isSelectedMultiSelect(['No', 'Other'])
    },
    isAdditionalOrOtherSelectedMultipleChoice() {
      // Get our selected answer option ID
      let optionId = this.question.answer.option

      // Find the option in answerOptions
      let answerOption = this.question.answerOptions.find(answerOption => answerOption.id == optionId)

      if(answerOption && answerOption.showAdditional)
        return true

      return this.isSelectedMultipleChoice(['Other'])
    },
    inputMask() {
      return this.$data[this.question.inputMask] || null
    },
    placeholder() {
      return this.question.placeholder || this.$gettext('Answer')
    },
    questionTarget() {
      const surveyTarget = (key) => `survey-question-${key}`
      const eventTarget = (key) => `event-question-${key}`

      const hasEventProperty = this.question.eventAnswerOptions !== undefined

      return hasEventProperty ?
        eventTarget(this.question.humanReadableId)
        : surveyTarget(this.question.humanReadableId)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/grant-circles';

h5 {
  font-size: 1.125rem;
  line-height: 1.5rem;
  margin-top: 5px;
}

.question-and-tooltip-wrapper {
  display: flex;
  flex-direction: row;
}

.question {
  margin-bottom: 1.5rem;

  &.light {
    .form-field-textarea {
      background: white;
    }
    .form-field-text {
      background: white;
    }
    select {
      background: white;
    }
  }
}

.description {
  cursor: pointer;
  display: block;
  width: 24px;

  .description-tooltip-wrapper {
    position: absolute;

    &:hover {
      .description-text {
        display: inline-block;
      }
    }
  }

  .description-text {
    border-radius: 4px;
    background-color: #f6f7f7;
    border: 1px solid #637381;
    color: #637381;
    display: none;
    font-size: 1rem;
    left: 2rem;
    line-height: 1.5rem;
    padding: 1rem;
    position: absolute;
    white-space: pre-wrap;
    width: 400px;
    z-index: 10;
    margin-left: -212px;
    top: 24px;
  }
}

.text {
  margin-bottom: 0;
}

#textarea {
  overflow-y: scroll;
  width: 100%;
  box-sizing: border-box;
  border: none;
  outline: none;
  font-size: 1rem;
  resize: none;
  padding: 0.5rem 0.5rem 0.5rem 0;
  max-height: 100px;
  min-width: 15rem;
  appearance: none;
  border-bottom: 1px solid $gray;
}

#md-textarea {
  width: 100%;
}
.introduction {
  margin: 0.5rem 0 0.5rem 0;
  font-style: italic;
  font-size: 0.9rem;
}

.full-name {
  display: flex;

  input:first-of-type {
    margin-right: 1rem;
  }
}

.email,
.phone {
  width: 50%;
}

.md-input {
  min-height: 57px;
}

.checkbox-field {
  background-color: rgba(0, 42, 139, 0.1);
  border-radius: 12px;
  padding: 1.5rem;

  .checkbox {
    margin-bottom: 0;
  }

  span.form-checkbox {
    display: inline-block;
    vertical-align: top;

    & + span {
      display: inline-block;
      width: 90%;
    }
  }
}

.ruby-font {
  color: $ruby;
}
</style>
